// this represents the object layer of the data layer
import * as db from './indexed-db';
import { emptyGuid } from '../other/api/guid';
import { IndexedDBAccess as IndexedDbAccess } from './indexed-db-wrapper';
import { INoSqlDb, JsonData } from './no-sql-db-intf';

export type guid = string;

export const stores = {
  all: 'all' as const
};

function createVersion1(idb: IDBDatabase) {
  db.newDbItemObjectStore(idb, {
    name: stores.all
  });
}

/** Special value: expire at end of day (next day invalid) */
export const nextDayExpiry = -100;

/** Helpers */
function toDate(d: Date | string): Date {
  return d instanceof Date ? d : new Date(d);
}

function sameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

export class NoSqlDb extends IndexedDbAccess implements INoSqlDb {
  public lastFetch: JsonData | null = null;
  private _dbName = 'wm-no-sql';

  public dbName(): string {
    return this._dbName;
  }

  public async clear(): Promise<void> {
    const conn = await this.connect();
    await db.deleteAll(conn, stores.all);
    this.delayedClose();
  }

  jsonDataExpired(jsonData: JsonData): boolean {
    return this.expired(jsonData.created, jsonData.expires);
  }

  async getJsonData(key: string, userId?: string, allowExpired: boolean = false): Promise<JsonData | null> {
    const idxKey = this.getId(key, userId ?? emptyGuid);
    const jsonData = await this.getFirst<JsonData>(stores.all, { query: idxKey });
    if (!jsonData) return null;

    // Coerce any string dates coming back from IDB
    const created = toDate(jsonData.created as any);
    const expires = jsonData.expires ?? 0;

    const expired = this.expired(created, expires);
    if (expired) {
      await this.del(idxKey, stores.all);
      return allowExpired ? { ...jsonData, created, expires } : null;
    }
    return { ...jsonData, created, expires };
  }

  async getItem<T>(key: string, userId?: string): Promise<T | null> {
    const jsonData = await this.getJsonData(key, userId);
    return (jsonData?.data as T) ?? null;
  }

  async delItem(key: string, userId?: string): Promise<void> {
    const idxKey = this.getId(key, userId ?? emptyGuid);
    await this.del(idxKey, stores.all);
  }

  async delGrabItem(key: string, userId?: string): Promise<void> {
    const grabKey = `grab://${key}`;
    const idxKey = this.getId(grabKey, userId ?? emptyGuid);
    await this.del(idxKey, stores.all);
  }

  async setItem<T>(key: string, value: T, expires?: number): Promise<void> {
    return this.internalSetItem(key, value, expires ?? 604800);
  }

  async setUserItem<T>(key: string, userId: string, value: T, expires?: number): Promise<void> {
    return this.internalSetItem(key, value, expires ?? 604800, userId);
  }

  /**
   * Fetch and cache a JSON file by URL (or return cached value).
   * `init` allows passing headers/method/etc. (CORS still enforced by browser).
   */
  async fetch<T>(
    url: string,
    expiresSeconds?: number,
    userId?: string,
    force?: boolean,
    init?: RequestInit
  ): Promise<T | null> {
    const lastFetch = !force ? await this.getJsonData(url, userId) : null;
    try {
      if (!lastFetch) {
        const result = await this.getJsonFile(url, init);
        if (result == null) return null;
        await this.setUserItem(url, userId ?? emptyGuid, result, expiresSeconds ?? 604800);
        return result as T;
      }
      return lastFetch.data as T;
    } finally {
      this.lastFetch = lastFetch;
    }
  }

  async grab<T>(
    itemName: string,
    action: () => Promise<T | null>,
    expiresSeconds?: number,
    userId?: string,
    force?: boolean
  ): Promise<T | null> {
    const url = `grab://${itemName}`;
    const lastFetch = await this.getJsonData(url, userId);
    try {
      if (force || !lastFetch || this.jsonDataExpired(lastFetch)) {
        try {
          const result = await action();
          if (result == null) return null;
          await this.setUserItem(url, userId ?? emptyGuid, result, expiresSeconds ?? 604800);
          return result;
        } catch {
          return null;
        }
      }
      return lastFetch.data as T;
    } finally {
      this.lastFetch = lastFetch;
    }
  }

  /**
   * Returns both old (cached) and new (fresh) values.
   */
  async grabOldAndNew<T>(
    itemName: string,
    action: () => Promise<T | null>,
    expiresSeconds?: number,
    userId?: string,
    force?: boolean
  ): Promise<{ old: T | null; new: T | null }> {
    const url = `grab://${itemName}`;

    // Always get cached (even if expired) as "old"
    const cachedData = await this.getJsonData(url, userId, true);
    const oldValue = (cachedData?.data as T) ?? null;

    try {
      if (force || !cachedData || this.jsonDataExpired(cachedData)) {
        try {
          const newValue = await action();
          if (newValue !== null) {
            await this.setUserItem(url, userId ?? emptyGuid, newValue, expiresSeconds ?? 604800);
          }
          return { old: oldValue, new: newValue };
        } catch {
          return { old: oldValue, new: null };
        }
      }
      return { old: oldValue, new: oldValue };
    } finally {
      this.lastFetch = cachedData ?? null;
    }
  }

  _upgrades = [{ version: 1, execute: createVersion1 }];
  // return a list of upgrades that get this database to the correct version
  protected getDatabaseUpgrades(): db.DatabaseUpgrade[] {
    return this._upgrades;
  }

  private getId(key: string, userId: string = emptyGuid): string {
    return `${userId}:${key}`;
  }

  private expired(createDate: Date | string, expiresSeconds: number): boolean {
    const created = toDate(createDate);
    if (expiresSeconds === nextDayExpiry) {
      const today = new Date();
      return !sameDay(today, created);
    }
    const now = Date.now();
    return created.getTime() + (expiresSeconds ?? 0) * 1000 < now;
  }

  private async internalSetItem<T>(
    key: string,
    value: T,
    expires: number = 604800,
    userId: string = emptyGuid
  ): Promise<void> {
    const item: JsonData = {
      id: this.getId(key, userId),
      data: value,
      created: new Date(),
      expires: expires
    };
    await super.put(item, stores.all);
  }

  private async getJsonFile(path: string, init?: RequestInit): Promise<unknown | null> {
    try {
      const response = await fetch(path, init);
      if (response.ok) {
        // Guard: Some endpoints return empty bodies with 204/200
        const text = await response.text();
        if (!text) return null;
        return JSON.parse(text);
      }
      return null;
    } catch {
      return null;
    }
  }

  // Force close all connections immediately
  public shutdown(): void {
    this.forceClose();
  }
}

declare global {
  // eslint-disable-next-line no-var
  var noSqlDb: NoSqlDb | undefined;
}

globalThis.noSqlDb = globalThis.noSqlDb || new NoSqlDb();

export const noSqlDb = globalThis.noSqlDb as NoSqlDb;

export function getNoSqlDb(): NoSqlDb {
  return globalThis.noSqlDb as NoSqlDb;
}

// Add event listener for page unload to ensure connections are closed
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (globalThis.noSqlDb) {
      globalThis.noSqlDb.shutdown();
    }
  });
}
